/**
 * The resolved car page's three states, and nothing else.
 *
 * It resolves nothing itself. `airflux-cards.js` reads the index, answers each
 * category and announces what it found; this file listens to that and decides
 * what the page around the cards should say — the heading, the years line, the
 * change-car escape, and whether the finder is asking or standing down.
 *
 * The split is deliberate. The cards are the same component the home page uses
 * and had to keep working with no page controller at all, so the resolver owns
 * the answering and this owns the page. Every number a customer reads here came
 * from one place.
 */
(() => {
  'use strict';
  if (window.AirfluxCarResult) return;

  const root = document.querySelector('[data-af-carpage]');
  if (!root) return;

  const RESOLVING = 'af-carpage-resolving';

  const els = {
    car: root.querySelector('[data-af-carpage-car]'),
    meta: root.querySelector('[data-af-carpage-meta]'),
    intro: root.querySelector('[data-af-carpage-intro]'),
    change: root.querySelector('[data-af-carpage-change]'),
    cards: root.querySelector('[data-af-carpage-cards]'),
    finder: root.querySelector('[data-af-carpage-finder]'),
  };

  /** The backstop for a paint that never came. */
  let painted = false;

  /**
   * The finder's visibility has ONE owner, and it is CSS.
   *
   * It has to be: the state is chosen before the first paint, so `display: none`
   * comes from a class on <html> that no `hidden` attribute set later can undo.
   * `is-open` is the override that beats it — "the customer asked for the panel
   * back" — and it is the only thing this file touches. Setting `hidden` here as
   * well produced a panel that was invisible for two different reasons and could
   * be brought back by neither.
   */
  const openFinder = (open) => els.finder?.classList.toggle('is-open', open);

  function apply(detail) {
    painted = true;
    const car = detail?.car ?? null;

    if (!car) {
      document.documentElement.classList.remove(RESOLVING);
      openFinder(false);
      if (els.cards) els.cards.hidden = true;
      if (els.change) els.change.hidden = true;
      if (els.car) els.car.textContent = '';
      if (els.meta) els.meta.hidden = true;
      return;
    }

    document.documentElement.classList.add(RESOLVING);
    if (els.car) els.car.textContent = car.named;

    // Years and generation, the way the Years dropdown and the header's band
    // both state them. The body is already inside `named`, and the version — the
    // customer's own answer — is too, so neither is repeated here.
    const meta = [car.years, car.generation].filter(Boolean).join(' · ');
    if (els.meta) {
      els.meta.textContent = meta;
      els.meta.hidden = !meta;
    }

    if (els.cards) els.cards.hidden = false;
    if (els.change) els.change.hidden = false;
    // A car arriving while the finder is open is the correction landing: it has
    // done its job, and the answer below it is now the thing to read.
    openFinder(false);
  }

  document.addEventListener('airflux:cards-painted', (event) => apply(event.detail));

  /**
   * 🔴 And the paint that already happened.
   *
   * `airflux-cards.js` is the script tag above this one, and on any visit where
   * the index is already in sessionStorage its whole pass — index, resolve,
   * announce — completes in the microtask drain between the two tags. The
   * listener above is then registered one tick after the only event it was ever
   * going to receive, and the page sits in its asking state under a heading that
   * says "What we make for your". Measured on a second page view, which is every
   * arrival from search.
   */
  if (window.AirfluxCards?.last) apply(window.AirfluxCards.last);

  els.change?.addEventListener('click', () => {
    if (!els.finder) return;
    openFinder(true);
    els.finder.scrollIntoView({
      block: 'center',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    // Onto the field itself, not the panel: the customer pressed a button that
    // means "let me type a different car", and the next thing they should be
    // able to do is type.
    const reg = els.finder.querySelector('[data-airflux-reg]');
    reg?.focus({ preventScroll: true });
  });

  /**
   * 🔴 The page must never be left mid-guess.
   *
   * The blocking script in the section adds `af-carpage-resolving` on the
   * strength of a `?car=` or a stored handle, before anything has been checked.
   * If the index fails, or the handle is one this index has never heard of, no
   * paint ever arrives — and the class would hold a heading reading "What we
   * make for your" with nothing after it, over a hidden finder, forever. On
   * `load` an unpainted page falls back to asking, which is the state it can
   * always honestly be in.
   */
  window.addEventListener('load', () => {
    if (!painted) apply(null);
  });

  window.AirfluxCarResult = { apply };
})();
